import { useId, type ReactNode } from 'react'
import { Link } from 'react-router'
import { ArrowUpRight } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import type { Surface } from '../../platform/async/surfaces'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { HeroTile, Panel } from '../../platform/primitives/Panel'
import '../../platform/notes.css'
import './overview.css'

type Props<T> = {
  title: string
  subtitle?: string
  /** The ↗ destination. Absent, there is no link. */
  href?: string
  hero?: boolean
  /** The body fills the cell's height (a chart that sizes itself to what is left). `<Async>` wraps its content in a block div, so the rule reaches that wrapper. */
  fill?: boolean
  /** Controls drawn in the heading row between the titles and the ↗ (never on the hero). */
  headExtra?: ReactNode
  surface: Surface
  /** A page can carry two synced surfaces; each names its own subject rather than repeating "Showing data from". */
  staleLabel?: (asOf: Date) => string
  state: AsyncState<T>
  skeleton: ReactNode
  empty: React.ComponentProps<typeof Async<T>>['empty']
  /** Called with the data and the heading's id (for a body that names itself by it); the widget never fetches. */
  children: (data: T, headingId: string) => ReactNode
}

/**
 * The shared frame of every Overview widget: a panel, a heading, a subtitle, an
 * optional ↗ and the body routed through `<Async>`. A failed widget stays in its
 * grid cell (R9) — the error renders inside this same box, so the grid does not
 * move.
 *
 * **The hero is composed the other way round.** `<Async>`'s error is an
 * `AlertPanel` (`--neg-soft`) and its skeleton a translucent sweep; both
 * composited over the lime `HeroTile` read 1.47:1 in dark (issue 118,
 * `negOnLime.test.tsx`). So for `hero` the `<Async>` sits OUTSIDE the tile: a
 * loading, empty or failed hero is a plain panel, and the lime tile exists only
 * around real data — with the stale note, if any, above it on the canvas.
 */
export function OverviewWidget<T>({ title, subtitle, href, hero = false, fill = false, headExtra, surface, staleLabel, state, skeleton, empty, children }: Props<T>) {
  const headingId = useId()
  // 44px below 640px and 33px above, the app's one answer; written inline because the lift is a JS hook, not a media query.
  const hit = useHitTarget('page')
  const headOf = (onLime: boolean) => (
    <div className="ov-widget__head">
      <div className="ov-widget__titles">
        <h2 id={headingId} className="ov-widget__title">
          {title}
        </h2>
        {subtitle !== undefined && <p className={onLime ? 'note ov-widget__sub ov-widget__sub--on-lime' : 'note ov-widget__sub'}>{subtitle}</p>}
      </div>
      {onLime ? null : headExtra}
      {href !== undefined && (
        <Link to={href} className="ov-widget__open" aria-label={`Open ${title}`} style={{ minInlineSize: hit, minBlockSize: hit }}>
          <ArrowUpRight size={17} aria-hidden />
        </Link>
      )}
    </div>
  )

  if (hero) {
    const hasData = state.status === 'success' || state.status === 'stale'
    return (
      <div role="region" aria-labelledby={headingId} className="ov-hero-cell">
        {hasData ? (
          <Async<T> state={state} surface={surface} staleLabel={staleLabel} skeleton={skeleton} empty={empty}>
            {(data) => (
              <HeroTile className="ov-widget">
                {headOf(true)}
                {children(data, headingId)}
              </HeroTile>
            )}
          </Async>
        ) : (
          <Panel className="ov-widget">
            {headOf(false)}
            <Async<T> state={state} surface={surface} staleLabel={staleLabel} skeleton={skeleton} empty={empty}>
              {(data) => children(data, headingId)}
            </Async>
          </Panel>
        )}
      </div>
    )
  }

  return (
    <Panel className="ov-widget" role="region" aria-labelledby={headingId}>
      {headOf(false)}
      {/* The list region: the cell clips, this scrolls (§0.7). */}
      <div className={fill ? 'ov-widget__body ov-widget__body--fill wg-scroll' : 'ov-widget__body wg-scroll'}>
        <Async<T> state={state} surface={surface} staleLabel={staleLabel} skeleton={skeleton} empty={empty}>
          {(data) => children(data, headingId)}
        </Async>
      </div>
    </Panel>
  )
}
